import { GoodAndServiceDetails } from 'app/src/features/Shopping/Direct/GoodAndService/components/GoodAndServiceDetails'
import { GoodAndServiceLeftPanelSide } from 'app/src/features/Shopping/Direct/GoodAndService/components/GoodAndServiceLeftPanelSide'
import { GoodAndServiceRightPanelSide } from 'app/src/features/Shopping/Direct/GoodAndService/components/GoodAndServiceRightPanelSide'
import {
  BuildBeneficiaries,
  cardPollingWorker,
  CardPurchaseStatus,
  getCardPurchaseStatus,
  type CardInProgressViewBaseProps,
  type CardPurchaseData,
} from 'app/src/features/Shopping/shared/utils'
import { shoppingApi, type portalConfigurationApi } from 'app/src/services/welfareportal'
import { BackToCatalogButton, ErrorPage, Image, PortalConfirmDialog } from 'app/src/shared/components'
import { formatDateDDMMYYYY } from 'app/src/shared/helpers'
import { useWebAppContext } from 'app/src/shared/hooks'
import successImage from 'app/static/images/success_img.png'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

function InProgressView(props: CardInProgressViewBaseProps) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GoodAndService' })
  const context = useWebAppContext()

  const shoppingConfig: Required<portalConfigurationApi.ShoppingConfig['cardPolling']> = useMemo(() => {
    const def_cardPollingInterval = 1000
    const def_cardTakeInChargeMaxCount = 10
    if (!context.portalConfiguration.shoppingConfig?.cardPolling)
      return {
        cardPollingInterval: def_cardPollingInterval,
        cardTakeInChargeMaxCount: def_cardTakeInChargeMaxCount,
      }
    else
      return {
        cardPollingInterval: context.portalConfiguration.shoppingConfig.cardPolling.cardPollingInterval ?? 1000,
        cardTakeInChargeMaxCount: context.portalConfiguration.shoppingConfig.cardPolling.cardTakeInChargeMaxCount ?? 10,
      }
  }, [context.portalConfiguration.shoppingConfig])

  useEffect(() => {
    cardPollingWorker(props.purchaseData, shoppingConfig, context).then(props.setPurchaseData)
  }, [context, props?.purchaseData, props?.setPurchaseData, shoppingConfig])

  return (
    <div>
      <h1 className='welfareportal-titlecenter'>{t('requestInProgressTitle')}</h1>
      <div className='mt-2 flex'>
        <div className='mx-auto'>
          <ProgressSpinner data-testid='loading-element' className='flex' />
          <div className='mt-4'>{t('requestInProgressDescription')}</div>
        </div>
      </div>
    </div>
  )
}

function FailedView() {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GoodAndService' })

  return (
    <ErrorPage errorMessage={t('requestInFailErrorMessage')}>
      <div className='mx-auto flex justify-center'>
        <BackToCatalogButton />
      </div>
    </ErrorPage>
  )
}

function TakeInChargeView() {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GoodAndService' })

  return (
    <div className='mt-2 flex'>
      <div className='mx-auto'>
        <Image
          pt={{ image: { className: 'object-fit mx-auto h-[150px] lg:h-[250px]' } }}
          src={successImage}
          alt="Immagine che descrive la presa in carico dell'operazione"
        />
        <div className='mx-auto' dangerouslySetInnerHTML={{ __html: t('takeInChargeMessage') }} />
        <div className='mx-auto flex justify-center'>
          <BackToCatalogButton />
        </div>
      </div>
    </div>
  )
}

function CompletedView(props: CardInProgressViewBaseProps) {
  return (
    <div className='mt-2 flex'>
      <div className='mx-auto'>
        <div className='mx-auto mt-4'>
          <GoodAndServiceDetails order={props.purchaseData.order} />
        </div>
      </div>
    </div>
  )
}

function DraftView(props: CardInProgressViewBaseProps) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.GoodAndService' })

  const [showConfirm, setShowConfirm] = useState(false)
  const context = useWebAppContext()
  const orderStartMutation = shoppingApi.useOrderStart()
  const [showUsageModal, setShowUsageModal] = useState(false)

  return (
    <>
      {showConfirm && (
        <PortalConfirmDialog
          accept={async () => {
            try {
              await orderStartMutation.mutateAsync({ pathParams: { orderId: props.purchaseData.order.orderId } })
              props.setPurchaseData({ status: CardPurchaseStatus.INPROGRESS, order: props.purchaseData.order })
            } catch (err) {
              context.logger?.error('Error While Starting Order', { orderId: props.purchaseData.order.orderId, error: err })
              props.setPurchaseData({ status: CardPurchaseStatus.FAILED, order: props.purchaseData.order })
            } finally {
              setShowConfirm(false)
            }
          }}
          reject={() => setShowConfirm(false)}
          body={t('confirmDialog.body')}
          title={t('confirmDialog.title')}
        />
      )}

      <div className='m-4 mx-auto max-w-5xl'>
        <h1 className='welfareportal-titlecenter'>{t('almostDoneTitle')}</h1>
        <div className='card grid grid-cols-1 divide-x-0 divide-y-2 p-5 lg:grid-cols-12 lg:divide-x-2 lg:divide-y-0'>
          <GoodAndServiceLeftPanelSide
            order={props.purchaseData.order}
            setShowUsageModal={setShowUsageModal}
            showUsageModal={showUsageModal}
          />
          <GoodAndServiceRightPanelSide order={props.purchaseData.order} showCredit />
        </div>
        <div className='mt-4 grid grid-rows-2 rounded-md bg-[var(--card-background)] p-4'>
          <div>
            <h3>{t('detailDescription')}</h3>
          </div>
          <div className='grid grid-cols-2'>
            <div className='grid-rows-2'>
              <div>{t('buyerDescription')}</div>
              <div className='mt-2'>
                <h3>{BuildBeneficiaries(props.purchaseData.order)}</h3>
              </div>
            </div>
            <div className='grid-rows-2'>
              <div>{t('buyDateDescription')}</div>
              <div className='mt-2'>
                <h3>{formatDateDDMMYYYY(new Date(), true)}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='m-6 text-center'>
        <Button rounded label={t('confirmButtonLabel')} onClick={() => setShowConfirm(true)} />
      </div>
    </>
  )
}

export function GoodAndServiceFunnel(props: { order: shoppingApi.OrderDetails }) {
  const [purchaseData, setPurchaseData] = useState<CardPurchaseData>()

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [purchaseData?.status])

  useEffect(() => {
    setPurchaseData({ order: props.order, status: getCardPurchaseStatus(props.order) })
  }, [props.order])

  return (
    <div>
      {purchaseData?.status === CardPurchaseStatus.DRAFT && (
        <DraftView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.INPROGRESS && (
        <InProgressView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.CANCELED && (
        <CompletedView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
      {purchaseData?.status === CardPurchaseStatus.FAILED && <FailedView />}
      {purchaseData?.status === CardPurchaseStatus.TAKEINCHARGE && <TakeInChargeView />}
      {(purchaseData?.status === CardPurchaseStatus.COMPLETED || purchaseData?.status === CardPurchaseStatus.USED) && (
        <CompletedView purchaseData={purchaseData} setPurchaseData={setPurchaseData} />
      )}
    </div>
  )
}
